import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { BASE_URL_API } from '../../References/url';
import AuthContext from '../../Store/auth-context';
import styles from './AllPoints.module.css';
import { BsFillArrowDownLeftSquareFill } from 'react-icons/bs';
import { motion } from 'framer-motion';

const AllPoints = () => {
    const authCtx = useContext(AuthContext);
    const [lastId, setLastId] = useState(null); // Cursor
    const [transactionData, setTransactionData] = useState([]);
    const [transactionType, setTransactionType] = useState('ALL');
    const [hasMoreData, setHasMoreData] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [loading, setLoading] = useState(true);

    // Set to track unique transaction IDs
    const transactionIdsSet = useRef(new Set());

    const getAllUsersPointsTransactions = useCallback(async () => {
        if (isFetching || !hasMoreData) return;

        setIsFetching(true);

        const response = await fetch(BASE_URL_API + '/getAllUsersPointsTransactions', {
            method: 'POST',
            body: JSON.stringify({
                token: authCtx.token,
                clientIdentifier: authCtx.clientIdentifier,
                lastId: lastId,
                transactionType: transactionType,
            }),
        });

        if (!response.ok) {
            console.error('Server error occurred!');
            setIsFetching(false);
            return;
        }

        const respo = await response.json();

        if (respo.status === 'success') {
            const fetchedData = respo.response || [];

            const uniqueNewData = fetchedData.filter((item) => {
                const isNew = !transactionIdsSet.current.has(item.ID);
                if (isNew) transactionIdsSet.current.add(item.ID);
                return isNew;
            });

            setTransactionData((prev) => [...prev, ...uniqueNewData]);

            if (respo.hasMore === false || fetchedData.length === 0) {
                setHasMoreData(false);
            }

            if (respo.nextCursor) {
                setLastId(respo.nextCursor);
            }
        } else if (respo.message === 'token Expired, please login again!') {
            authCtx.logout();
        } else {
            setHasMoreData(false);
        }

        setIsFetching(false);
        setLoading(false);
    }, [authCtx, lastId, transactionType, isFetching, hasMoreData]);

    const handleScroll = useCallback(() => {
        const scrollTop = window.scrollY;
        const windowHeight = window.innerHeight;
        const fullHeight = document.documentElement.offsetHeight;

        if (scrollTop + windowHeight + 100 >= fullHeight && !isFetching && hasMoreData) {
            getAllUsersPointsTransactions();
        }
    }, [getAllUsersPointsTransactions, isFetching, hasMoreData]);

    useEffect(() => {
        getAllUsersPointsTransactions();
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: i * 0.05,
                duration: 0.3,
                type: 'spring',
            },
        }),
    };

    return (
        <div className={styles.pageWrapper}>
            <div>
                <div>
                    <h2 className={styles.heading}>All Points Transactions</h2>
                </div>
                <div className={styles.txncount}>
                    {transactionData?.length} transactions found!
                </div>

            </div>

            {loading && transactionData.length === 0 && (
                <div className={styles.loading}>Loading...</div>
            )}

            {!loading && transactionData.length === 0 && (
                <div className={styles.noData}>No transactions found.</div>
            )}

            {transactionData.map((item, index) => (
                <motion.div
                    key={item.transactionId || index}
                    className={styles.eachTransactionWrapper}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    custom={index}
                >
                    <div>
                        {item.transactionType === 'DEBIT' ? (
                            <BsFillArrowDownLeftSquareFill size={22} className={styles.debitIcon} />
                        ) : (
                            <BsFillArrowDownLeftSquareFill size={22} className={styles.creditIcon} />
                        )}
                    </div>
                    <div className={styles.pointsValue}>
                        {item.transactionType === 'DEBIT' ? '-' : '+'} {item.pointsValue}
                    </div>
                    <div className={styles.clientUsername}>
                        {item.firstName} {item.lastName}
                    </div>
                    <div className={styles.transactionType}>
                        {item.transactionType}
                    </div>
                    <div className={styles.transactionDoneOn}>
                        {item.transactionDoneOn}
                    </div>
                    <div className={styles.remarks}>{item.remarks}</div>
                </motion.div>
            ))}

            {isFetching && <div className={styles.loading}>Loading more...</div>}

            {!hasMoreData && transactionData.length > 0 && (
                <div className={styles.noMore}>No more data available.</div>
            )}
        </div>
    );
};

export default AllPoints;
